import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { flatten } from '../utils/reactText';
type Kind = 'p' | 'h';
type Atom = {
  kind: Kind;
  text: React.ReactNode;
};
type Entry = {
  title: string;
  items: Atom[];
};
type Segment = string | {
  key: string;
  label: string;
};
type Variant = 'pharma' | 'finance';
type Dataset = {
  /** The prompt, with the load-bearing words marked for reveal. */
  prompt: Segment[];
  entries: Record<string, Entry>;
};
const PHARMA: Dataset = {
  prompt: ['\u201cDraft a ', {
    key: 'quick',
    label: 'quick'
  }, ' ', {
    key: 'reply',
    label: 'reply'
  }, ' to the ', {
    key: 'doctor',
    label: 'doctor'
  }, ' who asked about ', {
    key: 'dosing',
    label: 'dosing'
  }, '.\u201d'],
  entries: {
    quick: {
      title: 'QUICK \u2014 the word that kills review',
      items: [{
        kind: 'h',
        text: <>
              <b data-id="element-283" data-mp="m46djhps1cz">DON&rsquo;T</b> let &ldquo;quick&rdquo; skip review &mdash; the 24-hour turnaround covers exactly this
            </>
      }]
    },
    reply: {
      title: 'REPLY \u2014 external communication',
      items: [{
        kind: 'p',
        text: <>
              <b data-id="element-284" data-mp="27q2xv0h1dm23">DO</b> use approved language only &mdash; no improvised claims
            </>
      }, {
        kind: 'p',
        text: <>
              <b data-id="element-285" data-mp="2hfmf61p3p7uu">DO</b> attach the required disclosure, retain the thread for audit
            </>
      }, {
        kind: 'h',
        text: <>
              <b data-id="element-286" data-mp="2qh2pyajtvbzl">DO</b> write in med-info voice, never sales voice
            </>
      }]
    },
    doctor: {
      title: 'DOCTOR \u2014 an HCP audience',
      items: [{
        kind: 'p',
        text: <>
              <b data-id="element-287" data-mp="w5ny9o79b2h7">DO</b> hold fair balance &mdash; risks beside benefits
            </>
      }, {
        kind: 'h',
        text: <>
              <b data-id="element-288" data-mp="t28bbggkjqjp">DO</b> send the HCP format &mdash; two paragraphs, citations attached
            </>
      }]
    },
    dosing: {
      title: 'DOSING \u2014 on-label only',
      items: [{
        kind: 'p',
        text: <>
              <b data-id="element-289" data-mp="xzrumus4itii">DON&rsquo;T</b> paraphrase dosing &mdash; quote the label verbatim
            </>
      }]
    }
  }
};
const FINANCE: Dataset = {
  prompt: ['\u201cDraft a ', {
    key: 'reply',
    label: 'reply'
  }, ' to ', {
    key: 'client',
    label: 'Mrs. Webber'
  }, ' asking about ', {
    key: 'quarter',
    label: 'last quarter\u2019s'
  }, ' ', {
    key: 'returns',
    label: 'returns'
  }, '.\u201d'],
  entries: {
    reply: {
      title: 'REPLY \u2014 external communication',
      items: [{
        kind: 'p',
        text: <>
              <b data-id="element-290" data-mp="1qqrv4vr8xf5f">DO</b> use approved language &mdash; retail communications follow FINRA 2210
            </>
      }, {
        kind: 'p',
        text: <>
              <b data-id="element-291" data-mp="2ebkii0jyh16s">DO</b> retain the thread &mdash; books-and-records rules apply
            </>
      }, {
        kind: 'h',
        text: <>
              <b data-id="element-292" data-mp="25be48cgfnx6k">DO</b> write in advisory voice, never sales voice
            </>
      }]
    },
    client: {
      title: 'MRS. WEBBER \u2014 you know her',
      items: [{
        kind: 'p',
        text: <>
              <b data-id="element-293" data-mp="1hml8afzt9pby">DO</b> check suitability &mdash; the answer must fit her profile
            </>
      }, {
        kind: 'h',
        text: <>
              <b data-id="element-294" data-mp="1mjlaugxgc9yb">DO</b> lead with the downside &mdash; she carried losses in 2008; conservative profile, on record
            </>
      }]
    },
    quarter: {
      title: 'LAST QUARTER\u2019S \u2014 numbers need a source',
      items: [{
        kind: 'h',
        text: <>
              <b data-id="element-295" data-mp="2azktrn849mcz">DON&rsquo;T</b> send figures before the custodian statement posts &mdash; your number must match the
              one she gets
            </>
      }]
    },
    returns: {
      title: 'RETURNS \u2014 performance language',
      items: [{
        kind: 'p',
        text: <>
              <b data-id="element-296" data-mp="1wbzszzyprz88">DON&rsquo;T</b> state or imply guaranteed returns &mdash; no promissory claims, ever
            </>
      }]
    }
  }
};
const SETS: Record<Variant, Dataset> = {
  pharma: PHARMA,
  finance: FINANCE
};
export function SeeIt({
  showLink = true,
  variant = 'pharma'
}: {
  showLink?: boolean;
  variant?: Variant;
}) {
  const {
    prompt,
    entries
  } = SETS[variant];
  const [current, setCurrent] = useState<string | null>(null);
  const [found, setFound] = useState<string[]>([]);
  const reveal = (key: string) => {
    setCurrent(key);
    setFound(prev => prev.includes(key) ? prev : [...prev, key]);
  };
  const all = Object.values(entries).flatMap(e => e.items);
  const policyTotal = all.filter(i => i.kind === 'p').length;
  const houseTotal = all.filter(i => i.kind === 'h').length;
  const counted = found.flatMap(k => entries[k].items);
  const policy = counted.filter(i => i.kind === 'p').length;
  const house = counted.filter(i => i.kind === 'h').length;
  const entry = current ? entries[current] : null;
  return <div className="seeit" id="seeit" data-id="element-297" data-mp="367muezmav4qb">
      <div className="hint" data-id="element-298" data-mp="368oj46w8npqi">Hover a word &mdash; tap on mobile</div>
      <div className="sprompt" data-id="element-299" data-mp="3b6hncudze9o5">
        {prompt.map((seg, i) => typeof seg === 'string' ? <React.Fragment key={i} data-id="element-300" data-mp="3g7njz3arih2y">{seg}</React.Fragment> : <span key={i} className={`uw${found.includes(seg.key) ? ' found' : ''}`} role="button" tabIndex={0} onMouseEnter={() => reveal(seg.key)} onFocus={() => reveal(seg.key)} onClick={() => reveal(seg.key)} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          reveal(seg.key);
        }
      }} data-id="element-301" data-mp="qhtmouplbqhe">
              {seg.label}
            </span>)}
      </div>
      <div className="sreveal" aria-live="polite" data-id="element-302" data-mp="28sihpseb16zh">
        <div className="rw" data-id="element-303" data-mp="28ta021iw587t">{entry ? entry.title : 'Eleven words'}</div>
        {entry ? <ul data-id="element-304" data-mp="23vs0v2uoe7d0">
            {entry.items.map((item, i) => <li key={i} data-id="element-305" data-mp="2hllo5gqj8jkm" data-mpi={i}>
                <span className={`tag ${item.kind}`} data-id="element-306" data-mp="2hmwthkzmlzdu">{item.kind === 'p' ? 'POLICY' : 'HOUSE'}</span>
                <span data-id="element-307" data-mp="2ct2e2tr206kk">{item.text}</span>
              </li>)}
          </ul> : <div className="rd" data-id="element-308" data-mp="1p3htk6f95196">Four of them are load-bearing. The prompt wrote none of it down.</div>}
      </div>
      <div className="scount" data-id="element-309" data-mp="1l1d0b8spu6n2">
        11 words &middot; policy {policy}/{policyTotal} &middot; house {house}/{houseTotal}
      </div>
      <div className="send" data-id="element-310" data-mp="1fqr3rhqxj2ht">
        The POLICY atoms come from manuals and regulations. The HOUSE atoms come from your people
        &mdash; extracted, versioned, enforced the same way.
      </div>
      {/* The atoms only appear on hover, so crawlers and screen readers get the
          complete set here in the markup, worded to match what the panel shows. */}
      <div className="sr-only" data-id="element-311" data-mp="1tnxmjcbnl171">
        <p data-id="element-312" data-mp="1ypmwspr1xau2">
          Every obligation this prompt carries, revealed word by word above:{' '}
          {Object.values(entries).map(e => `${e.title} — ${e.items.map(i => `${i.kind === 'p' ? 'POLICY' : 'HOUSE'}: ${flatten(i.text)}`).join('; ')}`).join(' · ')}
        </p>
        <p data-id="element-313" data-mp="dbqrbb3v1lp5">
          The POLICY atoms come from manuals and regulations. The HOUSE atoms come from your people —
          extracted, versioned, enforced the same way.
        </p>
      </div>
      {showLink ? <Link className="slink" to="/how" data-id="element-314" data-mp="3lqzkeynz5czh">
          See the full mechanism &rarr;
        </Link> : null}
    </div>;
}